<script setup lang="ts">
// The shared centered-dialog shell for both apps. Built on Reka UI's Dialog so
// it carries focus-trap, scroll-lock, Escape, return-focus and the aria wiring
// (labelledby/modal) for free - the hand-rolled overlays it replaces only had
// `@click.self` + a manual Escape listener and no focus trap.
//
// API mirrors web-ui's old Modal.vue 1:1 so call sites are untouched: web-ui's
// Modal.vue now delegates here, and the extension's ConfirmDialog adopts it
// directly. Body content is the default slot; footer buttons go in #footer; a
// fully custom header goes in #header. The close (X) glyph comes from the
// injected icon set (useIcons) so this stays icon-library-agnostic.
import { computed, type Component } from 'vue';
import {
  DialogRoot,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogTitle,
  DialogClose,
  VisuallyHidden,
} from 'reka-ui';
import { useIcons } from '../icons';

// Optionals are explicitly `| undefined` so callers can bind optional-chained
// values (e.g. `:title="req?.title"`) under exactOptionalPropertyTypes.
const props = withDefaults(defineProps<{
  open: boolean;
  title?: string | undefined;
  /** Optional leading header icon (an injected/library icon component). */
  icon?: Component | undefined;
  /** While busy, backdrop click / Escape / the close button are inert. */
  busy?: boolean | undefined;
  size?: 'sm' | 'md' | 'lg' | undefined;
  /** Caller controls dismissal: backdrop + Escape do not close. */
  persistent?: boolean | undefined;
  /** Hide the header close (X). */
  hideClose?: boolean | undefined;
  /** `alertdialog` for destructive / must-acknowledge dialogs. */
  role?: 'dialog' | 'alertdialog' | undefined;
  /** Tone the header icon as destructive (red) instead of the accent. */
  danger?: boolean | undefined;
}>(), {
  busy: false,
  size: 'md',
  persistent: false,
  hideClose: false,
  role: 'dialog',
  danger: false,
});

const emit = defineEmits<{ (e: 'close'): void }>();

const icons = useIcons();

// Dismissal is locked while busy or persistent. Reka requests close via
// `update:open(false)` (Escape, outside-click, the X); we relay it as `close`
// only when allowed, and `open` stays controlled by the parent prop - so a
// blocked request simply leaves the dialog up, no preventDefault gymnastics.
const locked = computed(() => props.busy || props.persistent);
function onUpdateOpen(next: boolean): void {
  if (!next && !locked.value) emit('close');
}
function guard(e: Event): void {
  if (locked.value) e.preventDefault();
}
</script>

<template>
  <DialogRoot :open="open" @update:open="onUpdateOpen">
    <DialogPortal>
      <DialogOverlay class="hq-dialog__overlay" />
      <DialogContent
        :class="['hq-dialog', `hq-dialog--${size}`]"
        :role="role"
        :aria-describedby="undefined"
        @escape-key-down="guard"
        @pointer-down-outside="guard"
        @interact-outside="guard"
      >
        <header v-if="$slots.header || title" class="hq-dialog__head">
          <slot name="header">
            <component
              :is="icon"
              v-if="icon"
              :size="20"
              :class="['hq-dialog__icon', danger && 'hq-dialog__icon--danger']"
            />
            <DialogTitle class="hq-dialog__title">{{ title }}</DialogTitle>
            <DialogClose
              v-if="!hideClose"
              class="hq-dialog__close"
              aria-label="Close"
              :disabled="busy"
            >
              <component :is="icons.close" :size="16" />
            </DialogClose>
          </slot>
        </header>
        <!-- Reka warns without a title; keep one for a11y even when the caller
             renders its own header chrome. -->
        <VisuallyHidden v-else as-child>
          <DialogTitle>Dialog</DialogTitle>
        </VisuallyHidden>

        <slot />

        <footer v-if="$slots.footer" class="hq-dialog__footer">
          <slot name="footer" />
        </footer>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>

<!-- Unscoped: the overlay + content are portalled to <body> by Reka, so a
     scoped style hash would never reach them. Classes are `hq-dialog`-prefixed. -->
<style>
/* Portalled to <body>; design tokens resolve off :root in both apps. The
   data-state hooks are Reka's (open/closed) - we drive the enter/leave fades
   off them so there's no JS transition wrapper. */
.hq-dialog__overlay {
  position: fixed;
  inset: 0;
  background: var(--scrim);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: 200;
}
.hq-dialog__overlay[data-state="open"] { animation: hq-dialog-fade var(--dur-2, 0.18s) var(--ease-out, ease); }
/* Reka keeps the node mounted while a [data-state="closed"] animation runs, so
   this gives the graceful leave the hand-rolled <Transition> used to. */
.hq-dialog__overlay[data-state="closed"] { animation: hq-dialog-fade var(--dur-2, 0.18s) var(--ease-out, ease) reverse; }

.hq-dialog {
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 201;
  box-sizing: border-box;
  background: var(--surface-panel);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow-overlay);
  padding: var(--space-5);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  width: min(460px, 92vw);
  max-height: calc(100dvh - 2 * var(--space-6, 24px));
  overflow-y: auto;
}
.hq-dialog--sm { width: min(400px, 92vw); }
.hq-dialog--md { width: min(460px, 92vw); }
.hq-dialog--lg { width: min(640px, 94vw); }
.hq-dialog[data-state="open"] { animation: hq-dialog-in var(--dur-3, 0.22s) var(--ease-out, ease); }
.hq-dialog[data-state="closed"] { animation: hq-dialog-in var(--dur-2, 0.18s) var(--ease-out, ease) reverse; }

.hq-dialog__head {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: var(--space-3);
}
.hq-dialog__icon { flex-shrink: 0; color: var(--accent-default); }
.hq-dialog__icon--danger { color: var(--status-danger); }
.hq-dialog__title {
  margin: 0;
  grid-column: 2;
  font-size: var(--text-md);
  font-weight: 600;
  color: var(--ink-display);
}
.hq-dialog__close {
  grid-column: 3;
  background: none;
  border: 0;
  color: var(--ink-subtle);
  cursor: pointer;
  padding: var(--space-1);
  border-radius: var(--radius-input);
  line-height: 0;
  transition: background var(--dur-1) var(--ease-out), color var(--dur-1) var(--ease-out);
}
.hq-dialog__close:hover { background: var(--surface-sunken); color: var(--ink-primary); }
.hq-dialog__close:disabled { opacity: 0.5; cursor: default; }

.hq-dialog__footer {
  display: flex;
  gap: var(--space-2);
  justify-content: flex-end;
  margin-top: var(--space-1);
}

@keyframes hq-dialog-fade { from { opacity: 0; } }
@keyframes hq-dialog-in {
  from { opacity: 0; transform: translate(-50%, calc(-50% + 10px)) scale(0.97); }
}
@media (prefers-reduced-motion: reduce) {
  .hq-dialog[data-state],
  .hq-dialog__overlay[data-state] { animation: none; }
}
</style>
